import type { Location, Playlist, ReportSummary, Screen } from '@ghostscreen/contracts';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';

export type ReportScreenRow = {
  screenId: string;
  name: string;
  location: string | null;
  playlist: string | null;
  playedHours: number;
  plays: number;
};

export type ReportPlaylistBar = {
  playlistId: string;
  name: string;
  plays: number;
  widthPct: number;
};

export function buildReportScreenRows(input: {
  screens: Screen[];
  locations: Location[];
  playlists: Playlist[];
  assignmentByScreenId: Map<string, string | null>;
  summary: ReportSummary['screens'];
}): ReportScreenRow[] {
  const locationName = new Map(input.locations.map((location) => [location.id, location.name]));
  const playlistName = new Map(input.playlists.map((playlist) => [playlist.id, playlist.name]));
  const totals = new Map(input.summary.map((row) => [row.screenId, row]));

  return input.screens
    .filter((screen) => getCardConnectionStatus(screen) !== 'unpaired')
    .map((screen): ReportScreenRow => {
      const total = totals.get(screen.id);
      const playlistId = input.assignmentByScreenId.get(screen.id) ?? null;
      return {
        screenId: screen.id,
        name: screen.name,
        location: locationName.get(screen.locationId) ?? null,
        playlist: playlistId ? (playlistName.get(playlistId) ?? '') : null,
        playedHours: (total?.playedMs ?? 0) / 3_600_000,
        plays: total?.plays ?? 0,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildMostPlayedPlaylists(input: {
  summary: ReportSummary['playlists'];
  playlists: Playlist[];
}): ReportPlaylistBar[] {
  const playlistName = new Map(input.playlists.map((playlist) => [playlist.id, playlist.name]));
  const max = Math.max(1, ...input.summary.map((row) => row.plays));

  return input.summary.flatMap(({ playlistId, plays }) =>
    playlistId === null
      ? []
      : [
          {
            playlistId,
            name: playlistName.get(playlistId) ?? '',
            plays,
            widthPct: Math.round((plays / max) * 100),
          },
        ],
  );
}
